import IsAuth from './IsAuth';

export const ADMIN_ROLE = {
  ADMINISTRATOR: 0,
  VICE_ADMIN: 1,
  OPERATOR: 2,
} as const;

export const ADMIN_PERMISSION = {
  GENERAL_ADMIN: 'general_admin',
  ADMIN_USER_MANAGE: 'admin_user_manage',
  USER_POINT_VIEW: 'user_point_view',
  USER_POINT_HISTORY_VIEW: 'user_point_history_view',
  POINT_GRANT: 'point_grant',
  POINT_HISTORY_EDIT: 'point_history_edit',
  USER_DELETE: 'user_delete',
  IMPORT: 'import',
} as const;

export type AdminPermission =
  (typeof ADMIN_PERMISSION)[keyof typeof ADMIN_PERMISSION];

type AdminRole = (typeof ADMIN_ROLE)[keyof typeof ADMIN_ROLE];

type AdminTokenPayload = {
  user_id?: number;
  name?: string;
  role?: number;
  exp?: number;
};

const decodeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '=',
  );
  return window.atob(padded);
};

export const getAdminTokenPayload = (): AdminTokenPayload | null => {
  const token = IsAuth();
  if (!token) {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    return JSON.parse(decodeBase64Url(parts[1]));
  } catch (_error) {
    return null;
  }
};

export const getAdminRole = (): AdminRole | null => {
  const payload = getAdminTokenPayload();
  if (
    payload?.role === ADMIN_ROLE.ADMINISTRATOR ||
    payload?.role === ADMIN_ROLE.VICE_ADMIN ||
    payload?.role === ADMIN_ROLE.OPERATOR
  ) {
    return payload.role;
  }
  return null;
};

export const hasAdminPermission = (permission: AdminPermission): boolean => {
  const role = getAdminRole();

  switch (role) {
    case ADMIN_ROLE.ADMINISTRATOR:
      return true;
    case ADMIN_ROLE.VICE_ADMIN:
      switch (permission) {
        case ADMIN_PERMISSION.ADMIN_USER_MANAGE:
        case ADMIN_PERMISSION.POINT_GRANT:
        case ADMIN_PERMISSION.POINT_HISTORY_EDIT:
        case ADMIN_PERMISSION.USER_DELETE:
        case ADMIN_PERMISSION.IMPORT:
          return false;
        default:
          return true;
      }
    case ADMIN_ROLE.OPERATOR:
      switch (permission) {
        case ADMIN_PERMISSION.USER_POINT_VIEW:
        case ADMIN_PERMISSION.USER_POINT_HISTORY_VIEW:
          return true;
        default:
          return false;
      }
    default:
      return false;
  }
};

export const getDefaultAdminRoute = () => {
  const role = getAdminRole();
  if (role === ADMIN_ROLE.OPERATOR) {
    return '/user-point';
  }
  if (role === null) {
    return '/login';
  }
  return '/';
};

export const clearAdminSession = () => {
  localStorage.removeItem('accessToken');
};
